import { useEffect, useRef, useState } from "react";
import { X, PaperPlaneTilt, Sparkle } from "@phosphor-icons/react";
import api, { formatErr } from "@/lib/api";
import { useProjects } from "@/context/ProjectContext";

const SUGGESTIONS = [
  "Which package contributes most to embodied carbon?",
  "What's the carbon impact of replacing steel with CLT?",
  "What materials can reduce emissions by 30%?",
  "Estimate my carbon cost exposure for the next 12 months.",
];

export default function CopilotPanel({ open, onClose }) {
  const { active } = useProjects();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    if (!active) return;
    api
      .get(`/copilot/history?project_id=${active.id}`)
      .then((r) =>
        setMessages(
          r.data.items.flatMap((m) => [
            { role: "user", text: m.message },
            { role: "assistant", text: m.reply },
          ]),
        ),
      )
      .catch(() => {});
  }, [open, active]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setBusy(true);
    try {
      const { data } = await api.post("/copilot", { project_id: active?.id, message: msg });
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", text: `⚠ ${formatErr(e)}` }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div
        className={`fixed inset-0 z-40 bg-black/40 backdrop-blur-sm transition-opacity duration-200 ${
          open ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
        aria-hidden
      />
      <aside
        data-testid="copilot-panel"
        className={`fixed top-0 right-0 z-50 h-screen w-full max-w-md theme-bg backdrop-blur-xl border-l theme-border transition-transform duration-200 ease-out ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full">
          <header className="px-6 py-5 border-b theme-border flex items-start justify-between">
            <div>
              <div className="overline flex items-center gap-2">
                <Sparkle size={11} weight="fill" className="text-gold" /> AI Copilot
              </div>
              <h2 className="font-serif-display text-2xl mt-1">Carbon Copilot</h2>
              {active && (
                <div className="text-xs theme-ink-muted mt-1 truncate">Context: {active.name}</div>
              )}
            </div>
            <button
              data-testid="close-copilot-btn"
              onClick={onClose}
              className="theme-ink-muted hover:theme-ink transition-colors"
              aria-label="Close"
            >
              <X size={20} />
            </button>
          </header>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            {messages.length === 0 && (
              <div className="space-y-3">
                <p className="text-sm theme-ink-muted leading-relaxed">
                  Ask anything about embodied carbon, EPDs, EN15978 stages, or this project&apos;s
                  emissions profile. The Copilot grounds answers in your project data.
                </p>
                <div className="space-y-2 pt-2">
                  <div className="overline">Try</div>
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      data-testid={`copilot-suggestion-${s.slice(0, 18)}`}
                      onClick={() => send(s)}
                      className="block w-full text-left text-sm theme-ink/90 hover:text-gold border border theme-border hover:border-gold/40 px-3 py-2 rounded-sm transition-colors"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <div key={i} className={`text-sm ${m.role === "user" ? "theme-ink" : "theme-ink-muted"}`}>
                <div className="overline mb-1">{m.role === "user" ? "You" : "Copilot"}</div>
                <div className="whitespace-pre-wrap leading-relaxed">{m.text}</div>
              </div>
            ))}

            {busy && (
              <div className="text-sm">
                <div className="overline mb-1">Copilot</div>
                <div className="flex gap-1">
                  <span className="w-1.5 h-1.5 bg-gold rounded-full animate-pulse" />
                  <span className="w-1.5 h-1.5 bg-gold rounded-full animate-pulse [animation-delay:120ms]" />
                  <span className="w-1.5 h-1.5 bg-gold rounded-full animate-pulse [animation-delay:240ms]" />
                </div>
              </div>
            )}
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
            className="p-4 border-t theme-border flex gap-2"
          >
            <input
              data-testid="copilot-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about carbon impact..."
              className="flex-1 theme-surface border border theme-border px-3 py-2.5 text-sm theme-ink placeholder:theme-ink-dim rounded-sm focus:outline-none focus:border-gold transition-colors"
            />
            <button
              type="submit"
              data-testid="copilot-send-btn"
              disabled={busy}
              className="bg-gold text-black px-3 rounded-sm hover:bg-[#e0bc40] disabled:opacity-50 transition-colors"
            >
              <PaperPlaneTilt size={16} weight="fill" />
            </button>
          </form>
        </div>
      </aside>
    </>
  );
}
